import type { ApiSuccess } from './api';

export const RATE_CARD_UNITS = ['hour', 'day', 'week', 'month', 'block', 'head', 'event'] as const;
export type RateCardUnit = (typeof RATE_CARD_UNITS)[number];

export const PACKAGE_DURATION_UNITS = ['day', 'week', 'month'] as const;
export type PackageDurationUnit = (typeof PACKAGE_DURATION_UNITS)[number];

export const PACKAGE_ELIGIBILITY_KINDS = ['none', 'student_verification'] as const;
export type PackageEligibilityKind = (typeof PACKAGE_ELIGIBILITY_KINDS)[number];

export interface PackageBenefit {
  /** Stable within one package so the benefit can be identified after labels change. */
  id: string;
  title: string;
  /** Optional: `''` when the title says it all. */
  description: string;
}

export interface DeskPackageConfiguration {
  /** Unavailable packages remain in immutable history but cannot be selected for a new quote. */
  isAvailable: boolean;
  duration: {
    count: number;
    unit: PackageDurationUnit;
  };
  /** Null means access follows the facility's normal operating hours. */
  access: {
    /** ISO weekday numbers: Monday = 1 through Sunday = 7. */
    daysOfWeek: number[];
    /**
     * Minutes after midnight. Times an administrator sets are held to 12 PM – midnight in
     * half-hour steps (720–1440); a window saved before that limit keeps its times until changed.
     */
    startMinute: number;
    endMinute: number;
  } | null;
  eligibility: {
    kind: PackageEligibilityKind;
    /** Instructions shown to the administrator who verifies the customer. */
    instructions: string;
  };
  /** Currently descriptive benefits; no redemption balance is implied. */
  benefits: PackageBenefit[];
}

export interface RateCardItem {
  /** Stable business identifier retained across immutable versions. */
  id: string;
  name: string;
  amountMinor: number;
  unit: RateCardUnit;
  note: string;
  sortOrder: number;
}

export interface DeskPackageRateCardItem extends RateCardItem {
  package: DeskPackageConfiguration;
}

export interface AdminRateCard {
  id: string;
  code: string;
  versionId: string;
  versionNumber: number;
  currency: 'USD';
  changeSummary: string | null;
  spaceRates: RateCardItem[];
  deskPackages: DeskPackageRateCardItem[];
  addOns: RateCardItem[];
  activatedAt: string;
  activatedByUserId: string | null;
  createdAt: string;
}

export interface ReplaceAdminRateCardRequest {
  expectedVersionNumber: number;
  changeSummary?: string | null;
  spaceRates: Array<Omit<RateCardItem, 'sortOrder'>>;
  deskPackages: Array<
    Omit<DeskPackageRateCardItem, 'sortOrder' | 'package'> & {
      /** Omission preserves an existing package's rules; a new package receives safe defaults. */
      package?: DeskPackageConfiguration;
    }
  >;
  addOns: Array<Omit<RateCardItem, 'sortOrder'>>;
}

export type AdminRateCardResponse = ApiSuccess<AdminRateCard>;
